/**
 * InspectorSection — collapsible family-group wrapper for the Coaching Studio inspector
 *
 * Provides three decision-family containers: Focus, Custom Goals, and Scoring.
 * Each section has a sticky header with an icon and label, supports collapse/expand,
 * and persists state to localStorage so it survives page refreshes.
 *
 * Usage:
 *   <InspectorSection id="custom-goals" label="Custom Goals" icon={<GoalIcon />}>
 *     <DistanceGoalAnnotation ... />
 *     <MovementGoalAnnotation ... />
 *   </InspectorSection>
 *
 * Adding a new goal type (e.g., Joint Angle) into Custom Goals requires only adding
 * a new child element — no structural changes needed.
 */

import { useState, useEffect } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';

const LS_PREFIX = 'studio-inspector-';
const LS_SUFFIX = '-expanded';

interface InspectorSectionProps {
  /** Stable identifier used for localStorage key (e.g. "focus", "custom-goals", "scoring") */
  id: string;
  /** Human-readable family label shown in the section header */
  label: string;
  /** Optional icon element rendered left of the label */
  icon?: React.ReactNode;
  /** Section content — goal panels, emphasis controls, etc. */
  children: React.ReactNode;
  /** Tailwind accent color class for the section header label (e.g. "text-blue-400") */
  accentClass?: string;
  /** Additional CSS classes for the outer container */
  className?: string;
  /** data-testid for the section root element */
  testId?: string;
}

/**
 * InspectorSection renders a collapsible group with a sticky header and
 * localStorage-persisted expand/collapse state.
 */
export function InspectorSection({
  id,
  label,
  icon,
  children,
  accentClass = 'text-blue-400',
  className,
  testId,
}: InspectorSectionProps) {
  // Seed initial state from localStorage; fall back to expanded (true)
  const [isExpanded, setIsExpanded] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(`${LS_PREFIX}${id}${LS_SUFFIX}`);
      return stored !== null ? stored === 'true' : true;
    } catch {
      return true;
    }
  });

  // Persist to localStorage whenever the expanded state changes
  useEffect(() => {
    try {
      localStorage.setItem(`${LS_PREFIX}${id}${LS_SUFFIX}`, String(isExpanded));
    } catch {
      // localStorage unavailable (private browsing, storage quota) — non-fatal
    }
  }, [id, isExpanded]);

  return (
    <div
      className={cn('rounded-lg border border-border-subtle overflow-hidden', className)}
      data-testid={testId ?? `inspector-section-${id}`}
    >
      {/* Section header — sticky within the left column scroll container */}
      <button
        type="button"
        onClick={() => setIsExpanded((v) => !v)}
        className={cn(
          'sticky top-0 z-10 w-full flex items-center justify-between',
          'px-3 py-2 bg-dark-elevated-2 hover:bg-dark-elevated-3 transition-colors',
          'border-b border-border-subtle'
        )}
        aria-expanded={isExpanded}
        data-testid={`inspector-section-toggle-${id}`}
      >
        <div className="flex items-center gap-2">
          {icon && (
            <span className={cn('h-4 w-4 shrink-0 flex items-center', accentClass)} aria-hidden>
              {icon}
            </span>
          )}
          <span className={cn('text-xs font-semibold uppercase tracking-wider', accentClass)}>
            {label}
          </span>
        </div>
        {isExpanded ? (
          <ChevronUpIcon className="h-3.5 w-3.5 text-text-muted" aria-hidden />
        ) : (
          <ChevronDownIcon className="h-3.5 w-3.5 text-text-muted" aria-hidden />
        )}
      </button>

      {/* Section body */}
      {isExpanded && (
        <div className="space-y-3 p-2 bg-dark-card">
          {children}
        </div>
      )}
    </div>
  );
}

export default InspectorSection;
